import { useMemo } from "react";
import { useFetchScheduledEvents } from "./useFetchScheduledEvents";
import { useFetchUserLeagues } from "../league/useFetchUserLeagues";
import { isEventEligibleForLeague } from "../../utils/league/leagueHelpers";
import { hasEventPassed } from "../../utils/eventTimingHelper";

export const useEventsWithLeagueCount = () => {
  const {
    data: eventsData,
    isLoading: isEventsLoading,
    error: eventsError,
  } = useFetchScheduledEvents();

  const {
    data: leaguesData,
    isLoading: isLeaguesLoading,
    error: leaguesError,
  } = useFetchUserLeagues();

  const eventsWithLeagueInfo = useMemo(() => {
    if (!eventsData || !leaguesData?.memberOf) {
      return [];
    }

    // Get all leagues (not just active ones) for more flexible filtering
    const allUserLeagues = leaguesData.memberOf || [];

    return eventsData
      .filter((event) => !hasEventPassed(event.DateTime)) // Use Sunday 8pm PST cutoff instead of today
      .map((event) => {
        // Filter leagues based on event eligibility
        const eventLeagues = allUserLeagues.filter((league) => {
          // Don't show archived leagues
          if (league.status === "archived") {
            return false;
          }

          // Check if event is eligible for this league
          return isEventEligibleForLeague(
            event.EventId,
            league,
            event.DateTime,
          );
        });

        return {
          ...event,
          _id: event.EventId,
          userLeagueCount: eventLeagues.length,
          userLeagues: eventLeagues.map((league) => ({
            id: league._id,
            name: league.leagueName || league.name || "League",
            eventIds: league.eventIds || [], // Include eventIds for filtering
            startDate: league.startDate,
            endDate: league.endDate,
            status: league.status,
          })),
        };
      })
      .sort((a, b) => new Date(a.DateTime) - new Date(b.DateTime));
  }, [eventsData, leaguesData]);

  return {
    events: eventsWithLeagueInfo,
    isLoading: isEventsLoading || isLeaguesLoading,
    error: eventsError || leaguesError,
  };
};
